// THE SENTENCE THAT EXPLAINS ONCE, moved off the screen and kept reachable.
//
// # What this is for, and what it is NOT for
//
// The product accumulated explanatory prose one correct sentence at a time: a field
// hint saying what to type AND why riksu asks, a lede naming a statute AND arguing its
// consequence, a caveat under a figure. Each was right when it was added. The aggregate
// is a wall of text between a person and their work, which is what the product owner
// reported.
//
// The split is per STRING and per CLAUSE, never mechanical. Three kinds:
//
//   1. Text somebody ACTS ON — a format, a bound, what riksu will refuse, what to leave
//      empty, an absence that is a fact. It stays visible. It does not come here.
//   2. Text that EXPLAINS ONCE — the vocabulary, the citation, why a control is shaped
//      the way it is, the reasoning behind a derived figure. Useful the first time and
//      noise the hundredth. THAT is what this component is for.
//   3. Text that says nothing. It goes; it does not move here.
//
// It was measured before it was built: of `catalogue.ts`'s 87 field hints, 47 carry more
// than one sentence, and "the last sentence is the explanation" is WRONG in at least six
// of them — `setup.reportsToHint` ends on "Leave it empty at the top of the chart", which
// is an instruction, and `hire.identityHint` ends on the most consequential sentence on
// the whole form. So there is no heuristic here and deliberately no helper that derives
// the split: a call site names the two halves, or it keeps one.
//
// # A TOOLTIP IS A DECISION THAT THE FACT IS NOT REQUIRED
//
// This is the rule that governs the whole component, and it is the preview card's rule
// one surface along (`AGENTS.md`: "a card may never hold the only copy of a fact").
// Read out of Base UI's own source rather than assumed:
//
//   - `tooltip/trigger/TooltipTrigger.js` passes `mouseOnly: true` to its hover
//     interaction, so a TOUCH never opens it.
//   - it also wires `useFocus`, and `floating-ui-react/hooks/useFocus.js` gates on
//     `matchesFocusVisible` — so a KEYBOARD opens it and a touch tap does not, by either
//     path.
//   - and the package wires NO `aria-describedby` and NO `role="tooltip"` anywhere, the
//     same gap the preview card has.
//
// So a sighted person on a phone cannot reach this, and nothing may be put here that
// they need. That is exactly what kind (2) is, which is why the rule and the bucket
// agree — and it is why kind (1) is refused entry rather than merely discouraged.
//
// # THE ARIA IS RIKSU'S, AND IT IS NOT ON THE POPUP
//
// The obvious wiring — `aria-describedby` pointing at the popup — is wrong here, because
// the popup is PORTALLED and only exists while open. A description that resolves to
// nothing whenever the tooltip is shut is a description nobody ever receives, and it
// would look completely correct in the JSX.
//
// So the text lives TWICE and the always-present copy is the accessible one: a `sr-only`
// span mounted beside the trigger carries it, `aria-describedby` names that span, and the
// popup is `aria-hidden` so a reader browsing the open tooltip does not meet the same
// sentence a second time. A screen reader therefore gets the explanation on EVERY device
// including the touch screen the popup cannot reach — which is the half that makes the
// hover-only cost acceptable rather than merely accepted.
//
// # The trigger is a real button, and it is IN the tab order
//
// `useFocus` is what opens this for a keyboard, so the trigger has to be focusable; a
// `<span>` with a handler would be reachable by a mouse alone. That costs a tab stop per
// explanation, and the cost is deliberately not hidden with `tabIndex={-1}`: a form
// carrying twelve of these is an over-explained form, and the tab stops are the thing
// that makes that visible to whoever built it. Keeping them FEW is the design.
//
// The accessible name is the FIELD's, not a bare "help": twenty buttons all called
// "More information" is a list a screen-reader user cannot navigate.

import { InfoIcon } from "lucide-react";
import { useId } from "react";
import { useT } from "../shell/i18n";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

interface ExplainsProps {
  /**
   * What this explains — a field label, a heading, a figure's name. It becomes part of
   * the trigger's accessible name, so it is the RENDERED words rather than a key.
   */
  readonly about: string;
  /**
   * The sentence that explains once. Never a fact the reader has to act on: this is
   * unreachable on a touch screen, so anything required here is a fact somebody cannot
   * get. See the note at the top of this file.
   */
  readonly why: string;
}

/**
 * One explanation, on hover and on focus.
 *
 * Adding one is a DECLARATION — `Labelled`'s `why` member, or `Section`'s — rather than
 * this component wrapped by hand at a call site. It is exported because a heading or a
 * figure has no `Labelled` around it, not so that a screen can compose its own.
 */
export function Explains({ about, why }: ExplainsProps) {
  const t = useT();
  const describedBy = useId();

  return (
    <>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              // The name says WHICH thing is being explained. `aria-describedby` carries
              // the explanation itself, off the span below rather than off the popup.
              aria-label={t("explains.about", { field: about })}
              aria-describedby={describedBy}
              // `align-middle` rather than a flex parent: this sits inside a `<label>`
              // beside its text, and giving the label a flex context would break the
              // wrapping of a long field name.
              className="ms-1 inline-flex size-4 shrink-0 cursor-help items-center justify-center rounded-full align-middle text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
            >
              {/* DECORATIVE. The button's own `aria-label` is the name; an announced
                  glyph would read "info" in front of it.

                  It is a child of the RENDER ELEMENT rather than of `TooltipTrigger`,
                  which is the pattern `IconAction` already uses: Base UI's `render`
                  replaces the element it is given, and children handed to the trigger
                  instead are dropped. */}
              <InfoIcon aria-hidden className="size-3.5" />
            </button>
          }
        />
        {/* `aria-hidden` because the `sr-only` span below is the accessible copy. Without
            it the same sentence is met twice by anybody browsing the open tooltip.

            THE WIDTH IS THE LIBRARY'S `max-w-xs` and is deliberately not widened. The
            first version capped it at `--frame-prose`, which is 42rem — wider than the
            480px pane most of these stand in — so a one-line explanation drew a bar
            across the scrim and over the page behind it. Photographed on the New-position
            pane before it was changed. A tooltip is a short sentence; the reading measure
            that suits a paragraph is the wrong measure for one. */}
        <TooltipContent aria-hidden>{why}</TooltipContent>
      </Tooltip>
      {/* ALWAYS MOUNTED, and that is the point — see the note above. The portalled popup
          exists only while open, so it cannot be what `aria-describedby` names. */}
      <span id={describedBy} className="sr-only">
        {why}
      </span>
    </>
  );
}
